import type { FreightCommodity, FreightFulfillmentPlanHandlingUnitPreset } from '../../types/freight-responses';
import type { FreightPackagingType, LtlCommodity, LtlHandlingUnit } from '../../types/ltl';

// FreightFulfillmentPlanHandlingUnitPreset.weightUnitOfMeasureId / dimensionUnitOfMeasureId use
// the Freight domain's own numeric lookup (verified against WebV3Api.PublicContracts.Freight's
// mapper, e.g. FreightContractMapperTests) - NOT the generic V3Core WeightUnit/DimensionUnit
// enums, which number Pounds/Inches differently. Only Pounds/Inches are supported here today: no
// caller threads a seller's actual displayWeightUnit/displayDimensionUnit into this mapper yet, so
// a seller on Kilograms/Centimeters would still get a preset mislabeled as Pounds/Inches.
const POUNDS_WEIGHT_UOM_ID = 5;
const INCHES_DIMENSION_UOM_ID = 3;

/**
 * Maps a resolved FreightCommodity (from a saved preset's commodityPresetIds) to the wizard's
 * internal LtlCommodity, so it can be displayed when a preset is reopened for editing.
 */
export const freightCommodityToLtlCommodity = (fc: FreightCommodity): LtlCommodity => ({
  id: crypto.randomUUID(),
  commodityId: fc.commodityId ?? null,
  productName: fc.name ?? undefined,
  description: fc.description ?? '',
  freightClass: fc.freightClass ?? '',
  nmfcCode: fc.nmfcCode ?? '',
  pieceType: fc.packageType ?? undefined,
  numberOfPieces: fc.quantity,
  quantity: fc.quantity,
  weight: fc.weight ?? 0,
  weightUnit: fc.weightUnit !== 'Unknown' ? fc.weightUnit : undefined,
  isHazmat: fc.isHazmat,
  hazmatDetails: null,
  hazmatInfo: fc.hazmatInfo,
  insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
});

/**
 * Maps from the wizard's internal LtlHandlingUnit form state to the
 * FreightFulfillmentPlanHandlingUnitPreset API contract.
 *
 * Only non-zero / non-null values are written — zero dimensions and zero
 * quantity are treated as "not set" so the backend validator does not reject
 * otherwise-valid presets that omit optional fields.
 *
 * `doNotStack` maps from `!isStackable` per domain convention.
 *
 * `handlingUnitPresetId` is only included when provided (for update; omit for create).
 *
 * Commodities are persisted by reference — `commodityPresetIds` is the saved commodity preset
 * for each commodity row that has one selected. The backend resolves full commodity data from
 * these on read, so free-typed commodity fields with no preset behind them are not persisted.
 */
export const toHandlingUnitPresetPayload = (
  unit: LtlHandlingUnit,
  name: string,
  commodityPresetIds: number[],
  handlingUnitPresetId?: number,
): FreightFulfillmentPlanHandlingUnitPreset => {
  return {
    handlingUnitPresetId: handlingUnitPresetId ?? 0,
    name,
    packagingType: unit.packagingType != null ? (unit.packagingType as unknown as FreightPackagingType) : null,
    quantity: unit.quantity > 0 ? unit.quantity : null,
    weight: null,
    weightUnitOfMeasureId: POUNDS_WEIGHT_UOM_ID,
    length: unit.length > 0 ? unit.length : null,
    width: unit.width > 0 ? unit.width : null,
    height: unit.height > 0 ? unit.height : null,
    dimensionUnitOfMeasureId: INCHES_DIMENSION_UOM_ID,
    doNotStack: !unit.isStackable,
    commodityPresetIds,
  };
};
